/**
 * Shared analytics data hook: fetch + normalize + merge + S-curve fallbacks.
 * Used by Charts (via useAnalyticsChartData) and by Health, Critical Path, Milestones, Quality pages.
 */

import { useEffect, useState } from 'react';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import {
  fetchScheduleAnalytics,
  fetchRiskAnalytics,
  fetchProjectTasks,
  fetchProjectRelationships,
} from '@/api/analyticsService';
import { mergeAnalytics } from '@/utils/analyticsAdapter';
import {
  computeSCurveActivities,
  computeSCurveDuration,
  computeSCurveWBS,
  computeMilestoneOnTime,
  type WbsNodeForSCurve,
} from '@/utils/analyticsCompute';
import {
  normalizeActivities,
  countMissingRelations,
  countExcessiveLags,
  countHardConstraints,
  buildBaselineMap,
} from '@/utils/analyticsCompute';
import type { ScheduleActivity } from '@/types';

export interface UseProjectAnalyticsReturn {
  loading: boolean;
  merged: ReturnType<typeof mergeAnalytics> | null;
  scheduleAnalytics: Record<string, unknown> | null;
  activities: ScheduleActivity[];
  relationships: Record<string, unknown>[];
}

export function useProjectAnalytics(): UseProjectAnalyticsReturn {
  const { selectedProject } = useGlobalProject();
  const [loading, setLoading] = useState(false);
  const [merged, setMerged] = useState<ReturnType<typeof mergeAnalytics> | null>(null);
  const [scheduleAnalytics, setScheduleAnalytics] = useState<Record<string, unknown> | null>(null);
  const [activities, setActivities] = useState<ScheduleActivity[]>([]);
  const [relationships, setRelationships] = useState<Record<string, unknown>[]>([]);

  useEffect(() => {
    if (!selectedProject?.id) {
      setMerged(null);
      setScheduleAnalytics(null);
      setActivities([]);
      setRelationships([]);
      return;
    }
    let cancelled = false;
    const projectId = selectedProject.id;
    setLoading(true);
    // fetchScheduleAnalytics (schedule API) and fetchProjectTasks (activities endpoint) may resolve to
    // different snapshots if the backend uses import-scoped resolution for one but project-scoped for
    // the other. All client-side metrics (WBS hotspots, criticality, quality, milestones) use the
    // activities endpoint only, not the schedule analytics response. This is a known correctness
    // risk until snapshot scoping is unified across both APIs.
    (async () => {
      try {
        const [scheduleRes, riskRes, activitiesRes, relRes] = await Promise.allSettled([
          fetchScheduleAnalytics(projectId),
          fetchRiskAnalytics(projectId),
          fetchProjectTasks(projectId),
          fetchProjectRelationships(projectId),
        ]);
        if (cancelled) return;
        const scheduleObj = scheduleRes.status === 'fulfilled' ? scheduleRes.value : null;
        const risk = riskRes.status === 'fulfilled' ? riskRes.value : null;
        const tasksPayload = activitiesRes.status === 'fulfilled' ? activitiesRes.value : null;
        const normalized = normalizeActivities(tasksPayload?.activities ?? tasksPayload ?? []);
        setActivities(normalized);

        const rels: Record<string, unknown>[] = relRes.status === 'fulfilled' ? (relRes.value ?? []) : [];
        setRelationships(rels);

        const sCurveActivities = computeSCurveActivities(normalized);
        const sCurveDuration = computeSCurveDuration(normalized);
        const wbsNodes: WbsNodeForSCurve[] = (() => {
          const seen = new Set<string>();
          const nodes: WbsNodeForSCurve[] = [];
          for (const a of normalized) {
            const wbsId = String((a as Record<string, unknown>).wbs_id ?? (a as { wbs_id?: string }).wbs_id ?? '');
            if (!wbsId || seen.has(wbsId)) continue;
            seen.add(wbsId);
            const raw = a as Record<string, unknown>;
            nodes.push({
              wbs_id: wbsId,
              parent_wbs_id: (raw.parent_wbs_id ?? (a as { parent_wbs_id?: string }).parent_wbs_id) as string | null | undefined,
              wbs_name: (raw.wbs_name ?? (a as { wbs_name?: string }).wbs_name) as string | undefined,
            });
          }
          return nodes;
        })();
        const sCurveWbs = computeSCurveWBS(normalized, wbsNodes);
        const milestoneOnTime = computeMilestoneOnTime(normalized);

        // Use API schedule data only when it has actual points; otherwise fall back to client-computed
        // from activities. Backend may return empty arrays (e.g. [] for planned/actual), which would
        // otherwise prevent the client fallback from ever being used.
        const apiSc = scheduleObj?.s_curve_activities as { planned?: unknown[]; actual?: unknown[] } | undefined;
        const hasScActivities = Array.isArray(apiSc?.planned) && apiSc.planned.length > 0 || Array.isArray(apiSc?.actual) && apiSc.actual.length > 0;
        const apiSd = scheduleObj?.s_curve_duration as { planned?: unknown[]; earned?: unknown[] } | undefined;
        const hasScDuration = Array.isArray(apiSd?.planned) && apiSd.planned.length > 0 || Array.isArray(apiSd?.earned) && apiSd.earned.length > 0;
        const apiWbs = scheduleObj?.s_curve_wbs as { series?: Array<{ points?: unknown[] }> } | undefined;
        const hasScWbs = Array.isArray(apiWbs?.series) && apiWbs.series.length > 0 && apiWbs.series.some((s) => (s?.points?.length ?? 0) > 0);
        const apiMoT = scheduleObj?.milestone_on_time as { periods?: unknown[] } | undefined;
        const hasMilestoneOnTime = Array.isArray(apiMoT?.periods) && apiMoT.periods.length > 0;

        const effectiveSchedule: Record<string, unknown> = {
          ...(scheduleObj ?? {}),
          s_curve_activities: hasScActivities ? scheduleObj?.s_curve_activities : sCurveActivities,
          s_curve_duration: hasScDuration ? scheduleObj?.s_curve_duration : sCurveDuration,
          s_curve_wbs: hasScWbs ? scheduleObj?.s_curve_wbs : sCurveWbs,
          milestone_on_time: hasMilestoneOnTime ? scheduleObj?.milestone_on_time : milestoneOnTime,
          resource_demand_capacity: scheduleObj?.resource_demand_capacity ?? null,
        };
        setScheduleAnalytics(effectiveSchedule);

        const { pred: missingPred, succ: missingSucc } = countMissingRelations(rels, normalized);
        const excessiveLags = countExcessiveLags(rels);
        const constraintsCount = countHardConstraints(normalized);
        const baselineMap = buildBaselineMap(normalized);
        const result = mergeAnalytics(
          { project: null, schedule: scheduleObj, resources: null, risk },
          normalized,
          {
            missingPredecessorsCount: missingPred,
            missingSuccessorsCount: missingSucc,
            constraintsCount,
            excessiveLagsCount: excessiveLags,
            baselineOrOriginalByActivityId: Object.keys(baselineMap).length > 0 ? baselineMap : undefined,
          }
        );
        setMerged(result);
      } catch {
        if (!cancelled) setMerged(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProject?.id]);

  return {
    loading: loading || (!!selectedProject?.id && merged === null),
    merged,
    scheduleAnalytics,
    activities,
    relationships,
  };
}
